import React, { useState, useEffect } from "react";
import { Box, Text, keyframes } from "@chakra-ui/react";
import { useColorModeValue } from "@chakra-ui/react";

interface SignalLoadingStateProps {
  labelColor?: string;
  latestNoSignalReason?: string | null; // Latest reason from no_signal WebSocket messages
}

// Fade in animation
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Pulse animation for activity indicator
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const loadingMessages = [
  "Analyzing market conditions...",
  "Scanning for trading opportunities...",
  "Evaluating risk parameters...",
  "Monitoring price action...",
  "Assessing market regime...",
  "Waiting for optimal entry...",
  "Reviewing model predictions...",
  "Checking market volatility...",
];

const SignalLoadingState: React.FC<SignalLoadingStateProps> = ({ 
  labelColor,
  latestNoSignalReason = null,
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());

  const defaultLabelColor = useColorModeValue("gray.400", "gray.400");
  const textColor = labelColor || defaultLabelColor;
  const mutedColor = useColorModeValue("gray.500", "gray.500");

  // Format time as HH:MM AM/PM
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  // Rotate loading messages every 2.5 seconds
  useEffect(() => {
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 2500);

    return () => clearInterval(messageInterval);
  }, []);

  // Update current time every second
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timeInterval);
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={8}
      px={4}
      w="100%"
    >
      {/* Rotating loading message */}
      <Text
        key={currentMessageIndex}
        fontSize="sm"
        color={textColor}
        textAlign="center"
        fontFamily="'IBM Plex Sans', sans-serif"
        fontWeight="medium"
        mb={3}
        css={{
          animation: `${fadeIn} 0.5s ease-out`,
        }}
      >
        {loadingMessages[currentMessageIndex]}
      </Text>

      {/* Current time */}
      <Text
        fontSize="xs"
        color={mutedColor}
        textAlign="center"
        fontFamily="'IBM Plex Sans', sans-serif"
        mb={4}
      >
        {formatTime(currentTime)}
      </Text>

      {/* Minute status indicator - only when we have a reason from WebSocket */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={2}
        flexWrap="wrap"
      >
        {/* Activity indicator dot */}
        <Box
          w="6px"
          h="6px"
          borderRadius="full"
          bg="#3EEAEC"
        css={{
          animation: `${pulse} 2s ease-in-out infinite`,
        }}
        />
        {latestNoSignalReason && (
          <Text
            key={latestNoSignalReason}
            fontSize="xs"
            color={mutedColor}
            textAlign="center"
            fontFamily="'IBM Plex Sans', sans-serif"
            css={{
              animation: `${fadeIn} 0.3s ease-out`,
            }}
          >
            {latestNoSignalReason}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default SignalLoadingState;
